import ChevronLeft from "@mui/icons-material/ChevronLeft";
import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import { tabsOffset } from "components/TabLinks";
import { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { Footer } from "./components/Footer";
import { useAutoUpdateEmailSubjects } from "./hooks/useAutoUpdateEmailSubjects";
import { useCommunication } from "./hooks/useCommunication";
import { useCommunicationForm, useCommunicationFormContext } from "./hooks/useCommunicationForm";
import { DocumentsStep } from "./steps/DocumentsStep";
import { OrganizationStep } from "./steps/OrganizationStep";
import { PreviewStep } from "./steps/PreviewStep";
import { RecipientsStep } from "./steps/RecipientsStep";
import { ReportsStep } from "./steps/ReportsStep";

const CommunicationFormContent = () => {
  const [step, setStep] = useState<1 | 2>(1);
  const theme = useTheme();
  const backgroundColor = theme.palette.canvas;
  const navigate = useNavigate();
  const { mode } = useCommunicationFormContext();
  const view = mode === "view";

  // Auto-update email subjects for recipients when data changes
  // This must be registered here, not in the RecipientsStep component
  useAutoUpdateEmailSubjects();

  useEffect(() => {
    // start on step 2 in view state
    if (view) setStep(2);
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          background: backgroundColor,
          minHeight: {
            xs: `calc(100vh - ${theme.spacing(xsHeight + tabsOffset)})`,
            md: `calc(100vh - ${theme.spacing(mdHeight + tabsOffset)})`,
          },
        }}
      >
        <Box flexGrow={1} p={2}>
          <Card sx={{ boxShadow: "none" }}>
            <CardContent>
              <Stack direction="column" gap={3}>
                {view && (
                  <Box>
                    <Button startIcon={<ChevronLeft />} onClick={() => navigate("../status")}>
                      back to status checker
                    </Button>
                  </Box>
                )}
                <Box display="flex" justifyContent="space-between">
                  {view && <Typography variant="h5">Details</Typography>}
                  {!view && <Typography variant="h5">Send Mail</Typography>}
                  <Typography variant="caption" color="text.secondary">
                    Step {step} of 2
                  </Typography>
                </Box>

                {step === 1 ? (
                  <>
                    <OrganizationStep />
                    <ReportsStep />
                    <DocumentsStep />
                    <RecipientsStep />
                  </>
                ) : (
                  <PreviewStep />
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>
        <Footer step={step} nextStep={() => setStep(2)} back={() => setStep(1)} />
      </Box>
    </>
  );
};

export const CreateCommunications = () => {
  const { communicationId } = useParams<{ communicationId: string }>();
  const { communication, loading: communicationLoading } = useCommunication(communicationId);

  const methods = useCommunicationForm(communication);
  if (communicationLoading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Skeleton variant="rectangular" height={1000} />
      </Box>
    );

  return (
    <FormProvider {...methods}>
      <CommunicationFormContent />
    </FormProvider>
  );
};
